import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';

import { TableCell, TableCellLabel } from '@spotify-internal/data-table';
import {
  ButtonIcon,
  Chip,
  Flex,
  IconMore,
  Menu,
  MenuRoot,
  MenuRow,
  linkClassNames,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import {
  LabelChips,
  type LabelDefinition,
} from '@spotify/confidence-common-react';
import { DateUtils, ResourceNameLink } from '@spotify/confidence-core-react';
import {
  type CollectionEntryFragment,
  type IdentityRefFragment,
} from '@spotify/confidence-graphql';
import {
  EmptyCell,
  TruncatedText,
} from '@spotify/confidence-resource-data-table-react';
import { IdentityInline } from '@spotify/plugin-confidence-iam-react';

import {
  type CollectionEntryColumn,
  type CollectionEntryExtensionOptions,
  CollectionEntryOptions,
} from '../../extensions';
import { FavoriteIconButton } from '../FavoriteIconButton';

export type EntryToRemove = {
  name: string;
  displayName: string;
};

type EntryResource = CollectionEntryOptions['resource'];

export type CollectionEntryTableRow = {
  name: string;
  displayName: string;
  entry: CollectionEntryFragment;
  extension?: CollectionEntryExtensionOptions;
  resource?: EntryResource;
};

const getOwnerIdentity = (
  resource?: EntryResource,
): IdentityRefFragment | undefined => {
  if (!resource || !('owner' in resource) || !resource.owner) return undefined;
  return resource.owner.__typename === 'IamV1Identity'
    ? resource.owner
    : undefined;
};

const getLabels = (
  resource?: EntryResource,
): Array<{ key: string; value?: string | null }> | undefined => {
  if (!resource) return undefined;
  if ('labels' in resource && Array.isArray(resource.labels)) {
    return resource.labels;
  }
  return undefined;
};

const ChipList = ({ values }: { values: string[] }) => (
  <Flex gap="tighter-2" wrap="wrap">
    {values.map((v, i) => (
      <Chip key={i} size="small">
        {v}
      </Chip>
    ))}
  </Flex>
);

const ExtensionCellContent = ({
  column,
  resource,
}: {
  column: CollectionEntryColumn;
  resource: EntryResource;
}) => {
  if (column.Component) {
    return (
      <TableCell>
        <column.Component resource={resource} />
      </TableCell>
    );
  }

  const value = column.getValue?.(resource);
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return <EmptyCell />;
  }
  return (
    <TableCell>
      {Array.isArray(value) ? <ChipList values={value} /> : value}
    </TableCell>
  );
};

const columnHelper = createColumnHelper<CollectionEntryTableRow>();

export const createFavoriteColumn = (): ColumnDef<
  CollectionEntryTableRow,
  CollectionEntryTableRow
> =>
  columnHelper.accessor(row => row, {
    id: 'favorite',
    header: 'Favorite',
    size: 80,
    minSize: 80,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => (
      <TableCell>
        <FavoriteIconButton
          resourceName={row.original.name}
          resourceLabel={row.original.displayName}
         data-track-source-anchor="027ed26ae8;collection-entry-columns-favorite-icon-button"/>
      </TableCell>
    ),
  });

export const createNameColumn = (): ColumnDef<
  CollectionEntryTableRow,
  CollectionEntryTableRow
> =>
  columnHelper.accessor(row => row, {
    id: 'name',
    header: 'Name',
    minSize: 200,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => (
      <TableCell>
        <TruncatedText title={row.original.displayName}>
          <ResourceNameLink
            name={row.original.name}
            text={row.original.displayName}
            className={linkClassNames({ isStandalone: true })}
           data-track-source-anchor="5ac7b0e592;collection-entry-columns-resource-name-link"/>
        </TruncatedText>
      </TableCell>
    ),
  });

export const createTypeColumn = (): ColumnDef<
  CollectionEntryTableRow,
  CollectionEntryTableRow
> =>
  columnHelper.accessor(row => row, {
    id: 'type',
    header: 'Type',
    minSize: 120,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => {
      const { resource, extension } = row.original;
      const ResourceTypeLabel = extension?.ResourceTypeLabel;
      return (
        <TableCell>
          <TableCellLabel>
            {ResourceTypeLabel && resource ? (
              <ResourceTypeLabel resource={resource} />
            ) : (
              (extension?.resourceType ?? 'Unknown')
            )}
          </TableCellLabel>
        </TableCell>
      );
    },
  });

export const createOwnerColumn = (): ColumnDef<
  CollectionEntryTableRow,
  CollectionEntryTableRow
> =>
  columnHelper.accessor(row => row, {
    id: 'owner',
    header: 'Owner',
    minSize: 150,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => {
      const identity = getOwnerIdentity(row.original.resource);
      if (!identity) return <EmptyCell />;
      return (
        <TableCell>
          <IdentityInline identityRef={identity} />
        </TableCell>
      );
    },
  });

export const createLabelsColumn = (
  labelDefinitions: LabelDefinition[],
): ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow> =>
  columnHelper.accessor(row => row, {
    id: 'labels',
    header: 'Labels',
    minSize: 150,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => {
      const labels = getLabels(row.original.resource);
      if (!labels) return <EmptyCell />;
      return (
        <TableCell>
          <LabelChips labels={labels} labelDefinitions={labelDefinitions} />
        </TableCell>
      );
    },
  });

export const createAddedColumn = (): ColumnDef<
  CollectionEntryTableRow,
  CollectionEntryTableRow
> =>
  columnHelper.accessor(row => row, {
    id: 'added',
    header: 'Added',
    minSize: 120,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => (
      <TableCell>
        <TableCellLabel>
          {row.original.entry.createTime
            ? DateUtils.xAgo(new Date(row.original.entry.createTime))
            : '-'}
        </TableCellLabel>
      </TableCell>
    ),
  });

export const createExtensionColumn = (
  col: CollectionEntryColumn,
): ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow> =>
  columnHelper.accessor(row => row, {
    id: col.id,
    header: capitalize(lowerCase(col.id)),
    minSize: 120,
    enableSorting: false,
    enablePinning: false,
    cell: ({ row }) => {
      const { resource } = row.original;
      return resource ? (
        <ExtensionCellContent column={col} resource={resource} />
      ) : (
        <EmptyCell />
      );
    },
  });

export const createActionsColumn = (
  onRemoveEntry: (entry: EntryToRemove) => void,
): ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow> =>
  columnHelper.accessor(row => row, {
    id: 'actions',
    header: '',
    size: 56,
    enableSorting: false,
    enablePinning: false,
    enableHiding: false,
    cell: ({ row }) => (
      <TableCell>
        <MenuRoot>
          <ButtonIcon
            aria-label="Entry actions"
            size="small"
            variant="tertiary"
          >
            <IconMore size="small" />
          </ButtonIcon>
          <Popover hasNoArrow>
            <Menu aria-label="Entry actions">
              <MenuRow
                id="remove"
                textValue="Remove from collection"
                onAction={() =>
                  onRemoveEntry({
                    name: row.original.entry.name,
                    displayName: row.original.displayName,
                  })
                }
               data-track-source-anchor="6c6f9d4de4;;high">
                Remove from collection
              </MenuRow>
            </Menu>
          </Popover>
        </MenuRoot>
      </TableCell>
    ),
  });
